import { useCallback, useState } from 'react'
import { mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { AppIcon } from '../ui/icon'

/**
 * The folding sections of the thread's details column.
 *
 * Extracted from ConversationPanel so the triage card (spec 2026-09-11-06) can
 * be its own file and still look like the sections above it — the alternative
 * was a circular import between the panel and the card, or a second
 * nearly-identical section shell.
 *
 * Which sections are open is remembered per viewer. View state, not data: it
 * never reaches the URL, so a shared `?conversation=` link opens the same thread
 * for everyone regardless of how each of them likes their side panel.
 */
const DETAIL_SECTIONS_KEY = 'conversations.detailSections'

export type DetailSectionKey = 'contact' | 'assets' | 'tickets' | 'triage' | 'context' | 'knowledge' | 'tags'

function readOpenSections(): Record<string, boolean> {
    try {
        const raw = localStorage.getItem(DETAIL_SECTIONS_KEY)

        return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
    } catch {
        return {}
    }
}

/** Everything starts open — folding is an operator's choice, not a default. */
export function useSectionOpen(key: DetailSectionKey): [boolean, () => void] {
    const [open, setOpen] = useState<boolean>(() => readOpenSections()[key] !== false)
    const toggle = useCallback(() => {
        setOpen((previous) => {
            const next = !previous
            try {
                localStorage.setItem(DETAIL_SECTIONS_KEY, JSON.stringify({ ...readOpenSections(), [key]: next }))
            } catch {
                /* a viewer who blocks storage keeps the layout, just not the memory */
            }

            return next
        })
    }, [key])

    return [open, toggle]
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
    return <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>
}

export function DetailSection({
    sectionKey,
    title,
    trailing,
    children,
    testId,
}: {
    sectionKey: DetailSectionKey
    title: string
    /** A control that belongs to the header rather than to the body. */
    trailing?: React.ReactNode
    children: React.ReactNode
    testId: string
}) {
    const [open, toggle] = useSectionOpen(sectionKey)

    return (
        <section className="border-b border-border/60 pb-2 last:border-b-0" data-testid={testId}>
            <div className="flex items-center justify-between gap-2">
                <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                    className="flex items-center gap-1 py-1 min-w-0 text-left hover:text-foreground"
                    data-testid={`${testId}-toggle`}
                >
                    {open ? (
                        <AppIcon path={mdiChevronDown} size="xs" tone="muted" className="flex-shrink-0" aria-hidden />
                    ) : (
                        <AppIcon path={mdiChevronRight} size="xs" tone="muted" className="flex-shrink-0" aria-hidden />
                    )}
                    <SectionTitle>{title}</SectionTitle>
                </button>
                {trailing}
            </div>
            {open && <div className="space-y-2 pt-1">{children}</div>}
        </section>
    )
}
